import { GRAHA_HI_EN_BY_EN } from './jyotishaGrahaNames';

/** Short Devanagari labels for chart planet badges. */
const GRAHA_CHART_LABEL = {
  Sun: 'सू',
  Moon: 'चं',
  Mars: 'मं',
  Mercury: 'बु',
  Jupiter: 'गु',
  Venus: 'शु',
  Saturn: 'श',
  Rahu: 'रा',
  Ketu: 'के',
};

/**
 * @param {string} en
 * @param {number|string|null|undefined} [degree] Whole degree 0–29 within sign
 * @returns {{ id: string, label: string, degree?: number }}
 */
export function yogaChartPlanet(en, degree) {
  const g = GRAHA_HI_EN_BY_EN[en];
  const base = {
    id: en.toLowerCase(),
    label: GRAHA_CHART_LABEL[en] ?? (g?.hi ? g.hi.slice(0, 2) : en.slice(0, 2)),
  };
  if (degree !== '' && degree != null) {
    const d = Number(degree);
    if (Number.isInteger(d) && d >= 0 && d <= 29) {
      base.degree = d;
    }
  }
  return base;
}

/**
 * @param {number} house
 * @param {string[]} grahas
 */
export function yogaPlanetsInHouse(house, ...grahas) {
  return { [house]: grahas.map(yogaChartPlanet) };
}

/** @typedef {'conjunction' | 'aspect' | 'parivartan' | 'houses'} YogaFormationKind */

export const YOGA_FORMATION_META = {
  conjunction: {
    icon: '🔗',
    hi: 'युति · एक ही भाव',
    en: 'Same house (yuti)',
  },
  aspect: {
    icon: '👁',
    hi: 'दृष्टि',
    en: 'Aspect (drishti)',
  },
  parivartan: {
    icon: '↔',
    hi: 'परिवर्तन',
    en: 'Exchange (parivartan)',
  },
  houses: {
    icon: '🏠',
    hi: 'भाव स्वामी / भाव संबंध',
    en: 'House-lord link',
  },
};

/**
 * Teaching-sample chart layouts (not a real birth chart).
 * @type {Record<string, {
 *   formation: YogaFormationKind,
 *   formationNoteHi: string,
 *   formationNoteEn: string,
 *   highlightedHouses: number[],
 *   planetsByHouse: Record<number, { id: string, label: string }[]>,
 *   highlight: { dark: string, light: string },
 * }>}
 */
export const YOGA_CHART_DEMOS = {
  'raj-yog': {
    formation: 'aspect',
    formationNoteHi: 'केन्द्र (1,4,7,10) और त्रिकोण (1,5,9) के स्वामी जुड़ें - यहाँ 1 और 9 में ग्रह, दृष्टि से जुड़ाव दिखाया।',
    formationNoteEn: 'Kendra (1,4,7,10) and Trikona (1,5,9) lords connect - sample: grahas in 1 & 9 linked by aspect.',
    highlightedHouses: [1, 4, 5, 9, 10],
    planetsByHouse: {
      1: [yogaChartPlanet('Mars')],
      9: [yogaChartPlanet('Jupiter')],
      10: [yogaChartPlanet('Sun')],
    },
    highlight: { dark: 'rgba(251,191,36,0.3)', light: 'rgba(251,191,36,0.45)' },
  },
  'dhan-yog': {
    formation: 'conjunction',
    formationNoteHi: '2 और 11 के स्वामी एक भाव में (युति) - धन के भाव जुड़े।',
    formationNoteEn: 'Lords of 2nd and 11th together in one house (conjunction).',
    highlightedHouses: [2, 5, 9, 11],
    planetsByHouse: {
      2: [yogaChartPlanet('Venus'), yogaChartPlanet('Jupiter')],
      11: [yogaChartPlanet('Mercury')],
    },
    highlight: { dark: 'rgba(52,211,153,0.28)', light: 'rgba(16,185,129,0.4)' },
  },
  'daridra-yog': {
    formation: 'houses',
    formationNoteHi: '2 और 11 के स्वामी कमजोर/पीड़ित - धन भाव दबे (भाव संबंध)।',
    formationNoteEn: '2nd and 11th lords weak or afflicted - resource houses under pressure.',
    highlightedHouses: [2, 6, 8, 11, 12],
    planetsByHouse: {
      2: [yogaChartPlanet('Saturn')],
      11: [yogaChartPlanet('Rahu')],
    },
    highlight: { dark: 'rgba(148,163,184,0.25)', light: 'rgba(148,163,184,0.38)' },
  },
  'vipreet-raj-yog': {
    formation: 'parivartan',
    formationNoteHi: '6, 8, 12 के स्वामी परस्पर के भाव में (परिवर्तन) - त्रिक का विपरीत लाभ।',
    formationNoteEn: 'Lords of 6, 8, 12 in each other’s houses (mutual reception / parivartan).',
    highlightedHouses: [6, 8, 12],
    planetsByHouse: {
      6: [yogaChartPlanet('Saturn')],
      8: [yogaChartPlanet('Mars')],
      12: [yogaChartPlanet('Venus')],
    },
    highlight: { dark: 'rgba(244,63,94,0.28)', light: 'rgba(244,63,94,0.38)' },
  },
  'neech-bhang-raj-yog': {
    formation: 'aspect',
    formationNoteHi: 'नीच ग्रह (चंद्र) को स्वामी/केन्द्र से दृष्टि या सहायता - नीच भंग।',
    formationNoteEn: 'Debilitated graha (Moon) helped by lord or kendra support (aspect).',
    highlightedHouses: [1, 4, 8],
    planetsByHouse: {
      8: [yogaChartPlanet('Moon')],
      1: [yogaChartPlanet('Mars')],
      4: [yogaChartPlanet('Jupiter')],
    },
    highlight: { dark: 'rgba(217,70,239,0.28)', light: 'rgba(217,70,239,0.38)' },
  },
  'buddhi-yog': {
    formation: 'conjunction',
    formationNoteHi: 'बुध + गुरु एक ही भाव में - बुद्धि योग की युति।',
    formationNoteEn: 'Mercury + Jupiter in the same house (conjunction).',
    highlightedHouses: [5],
    planetsByHouse: {
      5: [yogaChartPlanet('Mercury'), yogaChartPlanet('Jupiter')],
    },
    highlight: { dark: 'rgba(139,92,246,0.3)', light: 'rgba(139,92,246,0.42)' },
  },
  'sanyas-yog': {
    formation: 'conjunction',
    formationNoteHi: 'कई ग्रह एक भाव में - संन्यास की ओर इशारा।',
    formationNoteEn: 'Many grahas in one house - classical sanyas hint.',
    highlightedHouses: [12],
    planetsByHouse: {
      12: [yogaChartPlanet('Saturn'), yogaChartPlanet('Ketu'), yogaChartPlanet('Mercury')],
    },
    highlight: { dark: 'rgba(34,211,238,0.26)', light: 'rgba(6,182,212,0.38)' },
  },
  'kemadruma-yog': {
    formation: 'houses',
    formationNoteHi: 'चंद्र के 2 और 12 में कोई ग्रह नहीं - अकेला चंद्र (क्लासिकल केमद्रुम)।',
    formationNoteEn: 'No planets in 2nd and 12th from Moon - “alone Moon” (classical Kemadruma).',
    highlightedHouses: [3, 4, 5],
    planetsByHouse: {
      4: [yogaChartPlanet('Moon')],
    },
    highlight: { dark: 'rgba(96,165,250,0.28)', light: 'rgba(96,165,250,0.4)' },
  },
  'vish-yog': {
    formation: 'conjunction',
    formationNoteHi: 'चंद्र + शनि एक भाव में - विष योग की युति।',
    formationNoteEn: 'Moon + Saturn in the same house (conjunction).',
    highlightedHouses: [7],
    planetsByHouse: {
      7: [yogaChartPlanet('Moon'), yogaChartPlanet('Saturn')],
    },
    highlight: { dark: 'rgba(100,116,139,0.32)', light: 'rgba(100,116,139,0.42)' },
  },
  'gaj-kesari-yog': {
    formation: 'houses',
    formationNoteHi: 'चंद्र और गुरु लग्न या चंद्र से केन्द्र में (यहाँ 1 और 4)।',
    formationNoteEn: 'Moon and Jupiter in kendra from Lagna or Moon (here houses 1 & 4).',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      1: [yogaChartPlanet('Moon')],
      4: [yogaChartPlanet('Jupiter')],
    },
    highlight: { dark: 'rgba(250,204,21,0.28)', light: 'rgba(250,204,21,0.42)' },
  },
  'ruchaka-yog': {
    formation: 'houses',
    formationNoteHi: 'मंगल स्वक्षेत्र/उच्च में केन्द्र (10) - रुचक महापुरुष।',
    formationNoteEn: 'Mars in own sign or exaltation in a kendra (house 10) - Ruchaka Mahapurush.',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      10: [yogaChartPlanet('Mars')],
    },
    highlight: { dark: 'rgba(167,139,250,0.32)', light: 'rgba(167,139,250,0.45)' },
  },
  'bhadra-yog': {
    formation: 'houses',
    formationNoteHi: 'बुध स्वक्षेत्र में केन्द्र (7) - भद्र महापुरुष।',
    formationNoteEn: 'Mercury in own sign in kendra (house 7) - Bhadra Mahapurush.',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      7: [yogaChartPlanet('Mercury')],
    },
    highlight: { dark: 'rgba(167,139,250,0.32)', light: 'rgba(167,139,250,0.45)' },
  },
  'hamsa-yog': {
    formation: 'houses',
    formationNoteHi: 'गुरु स्वक्षेत्र/उच्च में केन्द्र (4) - हंस महापुरुष।',
    formationNoteEn: 'Jupiter in own sign or exaltation in kendra (house 4) - Hamsa Mahapurush.',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      4: [yogaChartPlanet('Jupiter')],
    },
    highlight: { dark: 'rgba(167,139,250,0.32)', light: 'rgba(167,139,250,0.45)' },
  },
  'malavya-yog': {
    formation: 'houses',
    formationNoteHi: 'शुक्र स्वक्षेत्र/उच्च में केन्द्र (1) - मालव्य महापुरुष।',
    formationNoteEn: 'Venus in own sign or exaltation in kendra (house 1) - Malavya Mahapurush.',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      1: [yogaChartPlanet('Venus')],
    },
    highlight: { dark: 'rgba(167,139,250,0.32)', light: 'rgba(167,139,250,0.45)' },
  },
  'sasa-yog': {
    formation: 'houses',
    formationNoteHi: 'शनि स्वक्षेत्र/उच्च में केन्द्र (7) - शश महापुरुष।',
    formationNoteEn: 'Saturn in own sign or exaltation in kendra (house 7) - Sasa Mahapurush.',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      7: [yogaChartPlanet('Saturn')],
    },
    highlight: { dark: 'rgba(167,139,250,0.32)', light: 'rgba(167,139,250,0.45)' },
  },
  'mahapurush-group': {
    formation: 'houses',
    formationNoteHi:
      'पाँचों महापुरुष: मंगल, बुध, गुरु, शुक्र, शनि - प्रत्येक स्वक्षेत्र/उच्च में लग्न से केन्द्र (1, 4, 7, 10)। नमूने में सभी केन्द्र हाइलाइट।',
    formationNoteEn:
      'All five Mahapurush yogas: Mars, Mercury, Jupiter, Venus, Saturn - each in own sign or exaltation in a Kendra from Lagna (1, 4, 7, 10). Sample highlights all kendras.',
    highlightedHouses: [1, 4, 7, 10],
    planetsByHouse: {
      1: [yogaChartPlanet('Venus')],
      4: [yogaChartPlanet('Jupiter')],
      7: [yogaChartPlanet('Mercury'), yogaChartPlanet('Saturn')],
      10: [yogaChartPlanet('Mars')],
    },
    highlight: { dark: 'rgba(251,191,36,0.22)', light: 'rgba(251,191,36,0.38)' },
  },
};
